import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useStore } from 'zustand'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { Button } from '@quebon/ui/components/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@quebon/ui/components/dialog'
import { lcCoachingApi, type LessonCompletionScope } from '@/api/lcCoaching'
import { reviewNoteSourceKeys } from '@/hooks/coaching/useReviewNote'
import type { WorkbookMode } from '@/types/coaching'
import { getApiErrorMessage } from '@/utils/apiError'
import { hasHandheldDrafts, type HandheldDraftStore } from './handheldDraftStore'
import { HANDHELD_SECTIONS, type HandheldSection } from './handheldGrading'

const REQUIREMENTS = {
  COMPONENTS: null, BUILDUP_GRADES: 'buildup', BLOCK_QUIZ_RESPONSES: 'blockQuiz',
  ESSENTIAL_CHECKS: 'essentialCheck', WORKBOOK_GRADES: 'workbook', REVIEW_NOTE_COMPLETION_REQUIRED: 'reviewNote',
} satisfies Record<string, HandheldSection | null>
type Requirement = keyof typeof REQUIREMENTS
const requirementOrder = (code: Requirement) => {
  const section = REQUIREMENTS[code]
  return section == null ? -1 : HANDHELD_SECTIONS.indexOf(section)
}
function errorCode(e: unknown): string | null { return axios.isAxiosError(e) ? e.response?.data?.error?.code ?? null : null }
function missingFromError(e: unknown): Requirement[] {
  if (errorCode(e) === 'REVIEW_NOTE_COMPLETION_REQUIRED') return ['REVIEW_NOTE_COMPLETION_REQUIRED']
  const values: unknown = axios.isAxiosError(e) ? e.response?.data?.error?.details?.incompleteRequirements : null
  return Array.isArray(values) ? values.filter((v): v is Requirement => typeof v === 'string' && v in REQUIREMENTS) : []
}
interface Props {
 learnerId: number; lessonId: number; attemptId: number | null; completed: boolean; attendanceOpen: boolean; canEdit: boolean
 workbookMode?: WorkbookMode
 readinessVersion?: string
 store: HandheldDraftStore; onSelectRequirement: (section: HandheldSection) => void; onRefresh: () => Promise<unknown>
}
export function HandheldLessonCompletion({ learnerId, lessonId, attemptId, completed, attendanceOpen, canEdit, workbookMode = 'IMMEDIATE', readinessVersion = '', store, onSelectRequirement, onRefresh }: Props) {
  const { t } = useTranslation(['coaching', 'common'])
  const queryClient = useQueryClient()
  const saving = useStore(store, s => s.saving)
  const dirty = useStore(store, s => Object.values(s.scopes).some(scope => Object.keys(scope.drafts).length > 0))
  const [mode, setMode] = useState<'confirm' | 'missing' | 'retry' | null>(null)
  const [missing, setMissing] = useState<Requirement[]>([])
  const orderedMissing = [...missing].sort((a, b) => requirementOrder(a) - requirementOrder(b))
  const [error, setError] = useState<string | null>(null)
  const [completeSucceeded, setCompleteSucceeded] = useState(false)
  const [retryNeeded, setRetryNeeded] = useState(false)
  const advanceSucceeded = useRef(false)
  const scope: LessonCompletionScope = attemptId == null ? { attemptScope: 'PROGRESS' } : { attemptScope: 'REVIEW', learningAttemptId: attemptId }
  const readiness = useQuery({
    queryKey: ['coaching', 'learner', learnerId, 'lesson', lessonId, 'handheld-completion-readiness', attemptId ?? 'progress', workbookMode, readinessVersion],
    queryFn: () => lcCoachingApi.getLessonCompletionReadiness(learnerId, lessonId, workbookMode, scope),
    enabled: canEdit && attendanceOpen && !completed && !completeSucceeded && !dirty && !saving,
    retry: false,
    refetchOnWindowFocus: 'always',
  })
  const showComplete = !dirty && !saving && readiness.isSuccess && !readiness.isFetching && readiness.data.ready
  const allowed = canEdit && attendanceOpen && !dirty && !saving
  const begin = () => canEdit && attendanceOpen && !hasHandheldDrafts(store) && store.getState().beginSave()
  const loadReadiness = async () => {
    if (!begin()) return
    setError(null)
    try {
      const result = await lcCoachingApi.getLessonCompletionReadiness(learnerId, lessonId, workbookMode, scope)
      setMissing(result.incompleteRequirements.filter(code => code in REQUIREMENTS))
      setMode(result.ready ? 'confirm' : 'missing')
    } catch (e) {
      if (errorCode(e) === 'REVIEW_NOTE_COMPLETION_REQUIRED') { setMissing(['REVIEW_NOTE_COMPLETION_REQUIRED']); setMode('missing') }
      else setError(getApiErrorMessage(e, t))
    } finally { store.getState().endSave() }
  }
  const invalidateReviewNoteSources = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: reviewNoteSourceKeys.lessonLists(learnerId) }),
    queryClient.invalidateQueries({ queryKey: reviewNoteSourceKeys.previews(learnerId) }),
  ])
  const finish = async () => {
    if (!begin()) return
    setError(null)
    let didComplete = completeSucceeded
    try {
      if (!didComplete) {
        await lcCoachingApi.completeLesson(learnerId, lessonId, false, workbookMode, scope)
        didComplete = true
        setCompleteSucceeded(true)
        await invalidateReviewNoteSources()
      }
      if (attemptId == null && !advanceSucceeded.current) {
        try {
          await lcCoachingApi.advanceLesson(learnerId, false)
          await invalidateReviewNoteSources()
        }
        catch (e) { if (errorCode(e) !== 'NO_NEXT_LESSON') throw e }
        advanceSucceeded.current = true
      }
      await onRefresh()
      setRetryNeeded(false)
      setMode(null)
    } catch (e) {
      if (!didComplete && ['LESSON_NOT_COMPLETE', 'REVIEW_NOTE_COMPLETION_REQUIRED'].includes(errorCode(e) ?? '')) {
        let requirements = missingFromError(e)
        if (requirements.length === 0) {
          try {
            const response = await lcCoachingApi.getLessonCompletionReadiness(learnerId, lessonId, workbookMode, scope)
            requirements = response.incompleteRequirements.filter(code => code in REQUIREMENTS)
          } catch (readinessError) { requirements = missingFromError(readinessError) }
        }
        setMissing(requirements); setMode('missing'); void readiness.refetch()
      } else { setError(getApiErrorMessage(e, t)); setRetryNeeded(didComplete); setMode(didComplete ? 'retry' : null) }
    } finally { store.getState().endSave() }
  }
  return <div className="flex flex-col gap-2">
    {showComplete || retryNeeded || completed || completeSucceeded ? <Button className="min-h-11" disabled={!allowed || ((completed || completeSucceeded) && !retryNeeded)} onClick={() => void (retryNeeded ? finish() : loadReadiness())}>
      {retryNeeded ? t('mobileCompletion.retryAdvance') : completed || completeSucceeded ? t('lessonComplete.completed') : t('lessonComplete.button')}
    </Button> : null}
    {!completed && !completeSucceeded && !dirty && !saving && readiness.isSuccess && !readiness.isFetching && !readiness.data.ready ? <Button variant="outline" className="min-h-11" onClick={() => { setMissing(readiness.data.incompleteRequirements.filter(code => code in REQUIREMENTS)); setMode('missing') }}>{t('mobileCompletion.remaining')}</Button> : null}
    {readiness.isError && !dirty && !saving ? <div role="alert"><p>{getApiErrorMessage(readiness.error, t)}</p><Button variant="outline" onClick={() => void readiness.refetch()}>{t('roster.retry')}</Button></div> : null}
    {dirty ? <p className="text-sm text-muted-foreground">{t('handheldGrading.saveBeforeComplete')}</p> : null}
    {error && mode == null ? <p role="alert" className="text-destructive">{error}</p> : null}
    <Dialog open={mode != null} onOpenChange={open => { if (!open && !saving) setMode(null) }}>
      <DialogContent showCloseButton={false}>
        <DialogHeader><DialogTitle>{t(mode === 'missing' ? 'mobileCompletion.remaining' : mode === 'retry' ? 'mobileCompletion.saved' : 'lessonComplete.confirmTitle')}</DialogTitle>
          <DialogDescription>{t(mode === 'missing' ? 'mobileCompletion.remainingHelp' : mode === 'retry' ? 'mobileCompletion.retryHelp' : 'lessonComplete.confirmMessage')}</DialogDescription></DialogHeader>
        {mode === 'missing' ? <ul className="flex flex-col gap-3">{orderedMissing.length ? orderedMissing.map(code => <li key={code}>
          {REQUIREMENTS[code] ? <Button className="h-auto min-h-11 whitespace-normal" variant="outline" onClick={() => { setMode(null); onSelectRequirement(REQUIREMENTS[code]!) }}>{t(`mobileCompletion.requirement.${code}`)}</Button>
            : <p>{t('mobileCompletion.requirement.COMPONENTS')}</p>}
        </li>) : <li>{t('mobileCompletion.refreshRequired')}</li>}</ul> : null}
        {error ? <p role="alert" className="text-destructive">{error}</p> : null}
        <DialogFooter><Button className="min-h-11" variant="outline" disabled={saving} onClick={() => setMode(null)}>{t('lessonComplete.cancel')}</Button>
          {mode === 'confirm' || mode === 'retry' ? <Button className="min-h-11" disabled={!allowed} onClick={() => void finish()}>{t(mode === 'retry' ? 'roster.retry' : 'lessonComplete.confirmLabel')}</Button> : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
}
